<article class="doc">
  <header class="doc__header">
    <p class="doc__eyebrow">Atom</p>
    <h1 class="doc__title">Phone Input</h1>
    <p class="doc__lead">
      Campo de teléfono compuesto por un selector de lada (código de país) y un input para el número.
      El selector muestra únicamente el código numérico (<code>+52</code>, <code>+1</code>…) sin bandera.
      Borde normal <code>#dce0e8</code>, foco <code>#4169e2</code>.
    </p>
  </header>

  <!-- ── Estados ─────────────────────────────── -->
  <h2 class="doc-section-title">Estados</h2>

  <div class="doc-grid">
    <app-example-section
      title="Normal · Obligatorio"
      description="Estado por defecto con campo requerido."
    >
      <ui-phone-input [block]="true" label="Teléfono" [required]="true" />
    </app-example-section>

    <app-example-section
      title="Normal · Opcional"
      description="Muestra 'Opcional' a la derecha."
    >
      <ui-phone-input [block]="true" label="Teléfono" [required]="false" [showOptionalHint]="true" />
    </app-example-section>

    <app-example-section
      title="Error / inválido"
      description="Borde rojo. showMessage controla la visibilidad del mensaje de error."
    >
      <div class="doc-example-stack">
        <ui-phone-input
          [block]="true"
          label="Teléfono"
          [required]="true"
          [invalid]="isInvalid()"
          message="Ingresa un número de teléfono válido."
          [showMessage]="showErrorMsg()"
          [(callingCode)]="callingCode"
          [(phoneNumber)]="phoneNumber"
        />
        <div class="doc-toggle-row">
          <button type="button" class="doc-native-toggle" (click)="toggleInvalid()">
            {{ isInvalid() ? 'Quitar error' : 'Activar error' }}
          </button>
          <button type="button" class="doc-native-toggle" (click)="toggleErrorMsg()">
            {{ showErrorMsg() ? 'Ocultar mensaje' : 'Mostrar mensaje' }}
          </button>
        </div>
      </div>
    </app-example-section>

    <app-example-section
      title="Deshabilitado"
      description="Sin interacción; fondo atenuado."
    >
      <ui-phone-input [block]="true" label="Teléfono" [disabled]="true" />
    </app-example-section>

    <app-example-section
      title="Solo lectura"
      description="Muestra el valor sin permitir edición."
    >
      <ui-phone-input
        [block]="true"
        label="Teléfono"
        [callingCode]="'+52'"
        [phoneNumber]="'5500000000'"
        [readonly]="true"
        [showOptionalHint]="false"
      />
    </app-example-section>
  </div>

  <!-- ── Ejemplos de uso ─────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Ejemplos de uso</h2>

    <p class="doc-block__p">Binding bidireccional con signals:</p>
    <pre class="doc-block__pre" role="code"><code>callingCode = signal('+52');
phoneNumber = signal('');

&lt;ui-phone-input
  label="Teléfono"
  [required]="true"
  [(callingCode)]="callingCode"
  [(phoneNumber)]="phoneNumber"
/&gt;</code></pre>

    <p class="doc-block__p">Con Reactive Forms (wrapper manual):</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-phone-input
  label="Teléfono"
  [required]="true"
  [invalid]="form.controls.phone.invalid &amp;&amp; form.controls.phone.touched"
  message="Número requerido."
  [phoneNumber]="form.controls.phone.value ?? ''"
  (phoneNumberChange)="form.controls.phone.setValue($event)"
/&gt;</code></pre>

    <p class="doc-block__p">Estado inválido con mensaje controlado:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-phone-input
  label="Contacto"
  [required]="true"
  [invalid]="true"
  message="Número de teléfono inválido."
  [showMessage]="showMsg()"
/&gt;</code></pre>
  </section>

  <!-- ── Inputs ──────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Inputs</h2>
    <table class="doc-table">
      <thead>
        <tr>
          <th>Nombre</th>
          <th>Tipo</th>
          <th>Default</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>label</code></td>
          <td><code>string</code></td>
          <td><code>'Teléfono'</code></td>
          <td>Etiqueta visible sobre el campo.</td>
        </tr>
        <tr>
          <td><code>callingCode</code></td>
          <td><code>string</code> (model)</td>
          <td><code>'+52'</code></td>
          <td>Código de país seleccionado. Soporta <code>[(callingCode)]</code>.</td>
        </tr>
        <tr>
          <td><code>phoneNumber</code></td>
          <td><code>string</code> (model)</td>
          <td><code>''</code></td>
          <td>Número capturado. Soporta <code>[(phoneNumber)]</code>.</td>
        </tr>
        <tr>
          <td><code>placeholder</code></td>
          <td><code>string</code></td>
          <td><code>'0000000000'</code></td>
          <td>Texto guía en el campo del número.</td>
        </tr>
        <tr>
          <td><code>required</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Activa <code>aria-required</code> y muestra "Obligatorio".</td>
        </tr>
        <tr>
          <td><code>showOptionalHint</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Muestra "Opcional" cuando el campo no es requerido.</td>
        </tr>
        <tr>
          <td><code>requiredHint</code></td>
          <td><code>string</code></td>
          <td><code>'Obligatorio'</code></td>
          <td>Texto personalizable del indicador de campo requerido.</td>
        </tr>
        <tr>
          <td><code>optionalHint</code></td>
          <td><code>string</code></td>
          <td><code>'Opcional'</code></td>
          <td>Texto personalizable del indicador de campo opcional.</td>
        </tr>
        <tr>
          <td><code>disabled</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Deshabilita lada e input.</td>
        </tr>
        <tr>
          <td><code>readonly</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Solo lectura en el input del número.</td>
        </tr>
        <tr>
          <td><code>invalid</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Activa borde rojo y <code>aria-invalid</code>.</td>
        </tr>
        <tr>
          <td><code>message</code></td>
          <td><code>string | undefined</code></td>
          <td><code>undefined</code></td>
          <td>Texto del mensaje de error.</td>
        </tr>
        <tr>
          <td><code>showMessage</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Controla si el mensaje se renderiza. Permite validación silenciosa.</td>
        </tr>
        <tr>
          <td><code>name</code></td>
          <td><code>string | undefined</code></td>
          <td><code>undefined</code></td>
          <td>Atributo <code>name</code> del input del número.</td>
        </tr>
        <tr>
          <td><code>block</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Cambia el host a <code>display: block</code> para ocupar el 100%.</td>
        </tr>
        <tr>
          <td><code>fieldId</code></td>
          <td><code>string | undefined</code></td>
          <td>Auto-generado</td>
          <td>ID del input del número para la asociación con el <code>&lt;label&gt;</code>.</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- ── Outputs ─────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Outputs</h2>
    <table class="doc-table">
      <thead>
        <tr>
          <th>Nombre</th>
          <th>Tipo</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>callingCodeChange</code></td>
          <td><code>string</code></td>
          <td>Emite el nuevo código de país al cambiar el selector.</td>
        </tr>
        <tr>
          <td><code>phoneNumberChange</code></td>
          <td><code>string</code></td>
          <td>Emite el número actualizado en cada keystroke.</td>
        </tr>
        <tr>
          <td><code>blurred</code></td>
          <td><code>FocusEvent</code></td>
          <td>Emite cuando el input del número pierde el foco.</td>
        </tr>
        <tr>
          <td><code>focused</code></td>
          <td><code>FocusEvent</code></td>
          <td>Emite cuando el input del número recibe el foco.</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- ── Buenas prácticas ───────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Buenas prácticas</h2>
    <ul class="doc-list">
      <li>Usa <code>[(callingCode)]</code> y <code>[(phoneNumber)]</code> por separado para almacenar lada y número de forma independiente en el modelo.</li>
      <li>Para construir el valor final usa <code>&#96;&#36;&#123;callingCode()&#125; &#36;&#123;phoneNumber()&#125;&#96;</code> al momento de enviar el formulario.</li>
      <li>Activa <code>[invalid]</code> y <code>message</code> solo tras interacción del usuario (blur o submit), no en tiempo real.</li>
      <li>Usa <code>[showMessage]="false"</code> cuando necesites marcar el campo en rojo sin mostrar texto (p. ej. en tablas compactas).</li>
      <li>El <code>placeholder</code> por defecto asume formato mexicano; personalízalo según el país principal de tu formulario.</li>
    </ul>
  </section>
</article>
